import { useCallback, useRef, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Eye, EyeOff } from "lucide-react";
import { useClipboard } from "@/hooks/useClipboard";
import { useWidgetPageState } from "@/hooks/useWidgetPageState";
import { useI18n } from "@/lib/i18n";
import { stripMarkupAndMarkdown } from "@/lib/textUtils";
import { onPreviewRequest } from "@/events/previewBus";
import { addDataItem, addHistoryItem, createWidgetForPage, updateDataItem, getDataItem } from "@/lib/db";
import { emitDataChanged } from "@/hooks/useAppData";
import type { ClipboardPreview, DataItem, DataItemType } from "@/types";
import { toast } from "sonner";

import { PreviewActionButtons } from "@/components/areas/PreviewActionButtons";
import { PreviewContentRenderer } from "@/components/areas/PreviewContentRenderer";

const EDITABLE_TYPES: ClipboardPreview["type"][] = ['text', 'html', 'markdown', 'json', 'url'];

const PreviewArea = () => {
  const { t } = useI18n();
  const { preview, loading, error, readClipboard, handlePaste, copyToClipboard, clearPreview, setPreviewExternal } = useClipboard();
  const { currentPageId } = useWidgetPageState();
  const containerRef = useRef<HTMLDivElement>(null);
  const [editableText, setEditableText] = useState("");
  const [showRawSource, setShowRawSource] = useState(false);
  const [hasGainedFocus, setHasGainedFocus] = useState(false);

  // Monitor window focus to assume external clipboard check intent
  useEffect(() => {
    const onFocus = () => setHasGainedFocus(true);
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, []);

  // Listen for paste events on the container
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onPaste = (e: Event) => {
      const target = e.target as HTMLElement;
      if (target.tagName === 'TEXTAREA' || target.tagName === 'INPUT' || target.isContentEditable) {
        return;
      }
      handlePaste(e as ClipboardEvent);
    };
    el.addEventListener('paste', onPaste);
    return () => el.removeEventListener('paste', onPaste);
  }, [handlePaste]);

  // Listen for preview requests from widget clicks
  useEffect(() => {
    return onPreviewRequest((p) => setPreviewExternal(p));
  }, [setPreviewExternal]);

  // Sync editable text with current preview
  useEffect(() => {
    if (preview?.type === "text" || preview?.type === "html" || preview?.type === "markdown" || preview?.type === "json" || preview?.type === "url") {
      setEditableText(preview.content);
    } else {
      setEditableText("");
    }
    setShowRawSource(false);
  }, [preview]);

  // Handle click on the container
  const handleContainerClick = useCallback(async (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (target.tagName === 'TEXTAREA' || target.tagName === 'INPUT' || target.isContentEditable) {
      return;
    }

    const focusGained = hasGainedFocus;
    if (focusGained) {
      setHasGainedFocus(false);
    }

    if (!preview || preview.type === 'empty' || (focusGained && !preview.id)) {
      const latestPreview = await readClipboard();
      if (!latestPreview || latestPreview.type === 'empty') {
         setPreviewExternal({ type: 'text', content: '' });
         setShowRawSource(true);
      }
      return;
    }

    if ((preview.type === 'html' || preview.type === 'markdown' || preview.type === 'json' || preview.type === 'text' || preview.type === 'url') && !showRawSource) {
      const selection = window.getSelection();
      if (selection && selection.toString().length > 0) {
        return;
      }
      setShowRawSource(true);
    }
  }, [preview, hasGainedFocus, readClipboard, showRawSource, setPreviewExternal]);

  const generateLabel = (type: DataItemType): string => {
    const now = new Date();
    const yy = String(now.getFullYear()).slice(2);
    const MM = String(now.getMonth() + 1).padStart(2, '0');
    const dd = String(now.getDate()).padStart(2, '0');
    const hh = String(now.getHours()).padStart(2, '0');
    const mm = String(now.getMinutes()).padStart(2, '0');
    const timestamp = `${yy}.${MM}.${dd}|${hh}:${mm}`;
    const prefix = type === 'image' ? 'image' : type === 'table' ? 'table' : 'text';
    return `(${prefix})${timestamp}`;
  };

  const handleSave = useCallback(async () => {
    if (!preview || preview.type === 'empty') return;
    const itemType: DataItemType = preview.type === 'image' ? 'image' : preview.type === 'url' ? 'url' : 'text';
    const mimeType = preview.type === 'html' ? 'text/html' : undefined;
    const isEditable = EDITABLE_TYPES.includes(preview.type);
    const saveContent = isEditable ? editableText : preview.content;
    const now = Date.now();
    const id = crypto.randomUUID();
    const label = generateLabel(itemType);
    const dataItem: DataItem = { id, type: itemType, label, content: saveContent, mimeType, tags: [], createdAt: now, updatedAt: now };
    try {
      await addDataItem(dataItem);
      await createWidgetForPage(id, currentPageId);
      await addHistoryItem({ id: crypto.randomUUID(), dataId: id, createdAt: now });
      const clipType = (preview.type === 'html' || preview.type === 'markdown') ? preview.type : 'text';
      await copyToClipboard(saveContent, clipType);
      emitDataChanged();
      toast.success(t('preview.save') + ' ✓');
      clearPreview();
    } catch (err) {
      console.error('Save failed:', err);
    }
  }, [preview, editableText, currentPageId, copyToClipboard, clearPreview, t]);

  const stripFormatting = useCallback(() => {
    if (!preview || (preview.type !== 'html' && preview.type !== 'text' && preview.type !== 'markdown' && preview.type !== 'json' && preview.type !== 'url')) return;
    const raw = (preview.type === 'text' || preview.type === 'html' || preview.type === 'markdown' || preview.type === 'json' || preview.type === 'url') ? editableText : preview.content;
    const plainText = stripMarkupAndMarkdown(raw);
    setEditableText(plainText);
    setPreviewExternal({ ...preview, type: 'text', content: plainText });
    toast.success(t('preview.format_removed') || '문법 제거됨');
  }, [preview, editableText, setPreviewExternal, t]);

  const handleOverwrite = useCallback(async () => {
    if (!preview?.id || (preview.type !== 'html' && preview.type !== 'text' && preview.type !== 'markdown' && preview.type !== 'json' && preview.type !== 'url')) return;
    try {
      const existingData = await getDataItem(preview.id);
      if (!existingData) {
        toast.error('Original data not found');
        return;
      }
      await updateDataItem({ ...existingData, content: editableText, updatedAt: Date.now() });
      const clipType = (preview.type === 'html' || preview.type === 'markdown') ? preview.type : 'text';
      await copyToClipboard(editableText, clipType);
      emitDataChanged();
      toast.success(t('preview.overwrite') + ' ✓');
      setPreviewExternal({ ...preview, content: editableText });
    } catch (err) {
      console.error('Overwrite failed:', err);
      toast.error('Overwrite failed');
    }
  }, [preview, editableText, copyToClipboard, setPreviewExternal, t]);

  const hasSaveable = preview && preview.type !== 'empty' && preview.type !== 'unsupported' && (preview.type === 'image' || editableText.trim() !== "");
  const hasTextContent = preview && (preview.type === 'text' || preview.type === 'html' || preview.type === 'markdown' || preview.type === 'json' || preview.type === 'url');

  return (
    <motion.section
      initial={{ y: -10, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.5, delay: 0.1 }}
      className="glass-panel rounded-2xl p-4 flex gap-4 h-full min-h-0"
    >
      <PreviewActionButtons
        handleSave={handleSave}
        handleOverwrite={handleOverwrite}
        stripFormatting={stripFormatting}
        hasSaveable={hasSaveable}
        hasTextContent={hasTextContent}
        hasId={!!preview?.id}
      />

      <div className="relative flex-1 h-full min-h-0">
        <div
          ref={containerRef}
          tabIndex={0}
          onClick={handleContainerClick}
          data-testid="preview-content"
          className={`h-full min-h-0 rounded-xl bg-background/40 border border-border/50 flex px-4 pt-1.5 pb-4 overflow-auto transition-all focus:outline-none focus:ring-2 focus:ring-ring/50 ${
            !preview || preview.type === 'empty' || preview.type === 'image' || preview.type === 'unsupported'
              ? 'items-center justify-center cursor-pointer'
              : 'items-start'
          }`}
        >
          <PreviewContentRenderer
            loading={loading}
            error={error}
            preview={preview}
            showRawSource={showRawSource}
            editableText={editableText}
            setEditableText={setEditableText}
            setShowRawSource={setShowRawSource}
          />
        </div>

        {(preview?.type === 'html' || preview?.type === 'markdown' || preview?.type === 'json' || preview?.type === 'url' || preview?.type === 'text') && (
          <button
            onClick={() => setShowRawSource((v) => !v)}
            className={`absolute bottom-2 left-2 inline-flex items-center gap-1 rounded-lg px-2 py-1 text-[10px] font-medium transition-colors backdrop-blur-sm ${
              showRawSource
                ? 'bg-primary/15 text-primary border border-primary/30'
                : 'bg-background/60 text-muted-foreground border border-border/50 hover:text-foreground hover:bg-background/80'
            }`}
          >
            {showRawSource ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
            {showRawSource ? (t('preview.rendered') || '렌더링') : (t('preview.raw_source') || '원문')}
          </button>
        )}
      </div>
    </motion.section>
  );
};

export default PreviewArea;
